// src/pages/elibrary/EditSubtopic.tsx
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fireDB } from '../../../../Backend/Firebase/FirebaseConfigData';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

interface Subtopic {
  title: string;
  author?: string;
  driveLink?: string;
  blogLink?: string;
  metadata?: string;
  dataType?: string;
}

const EditSubtopic = () => {
  const { id, subId } = useParams<{ id: string; subId: string }>();
  const navigate = useNavigate();
  const [subtopic, setSubtopic] = useState<Subtopic | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubtopic = async () => {
      try {
        if (!id || !subId) return;

        const ref = doc(fireDB, 'libraryEntries', id, 'subtopics', subId);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          setSubtopic(snap.data() as Subtopic);
        } else {
          alert('Subtopic not found');
          navigate(`/elibrary/update/${id}`);
        }
      } catch (error) {
        console.error('Error fetching subtopic:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubtopic();
  }, [id, subId, navigate]);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !subId || !subtopic) return;

    try {
      const ref = doc(fireDB, 'libraryEntries', id, 'subtopics', subId);
      await updateDoc(ref, subtopic);

      alert('Subtopic updated successfully!');
      navigate(`/elibrary/update/${id}`);
    } catch (error) {
      console.error('Error updating subtopic:', error);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-screen">
        <p>Loading subtopic...</p>
      </div>
    );

  if (!subtopic)
    return (
      <div className="text-center mt-20 text-gray-600">
        <p>Subtopic not found.</p>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <form
        onSubmit={handleUpdate}
        className="bg-white shadow-md rounded-lg p-6 max-w-2xl mx-auto"
      >
        <h2 className="text-2xl font-semibold mb-4 text-center">✏️ Edit Subtopic</h2>

        <label className="block mb-2 font-medium">Title</label>
        <input
          type="text"
          value={subtopic.title}
          onChange={(e) => setSubtopic({ ...subtopic, title: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
          required
        />

        <label className="block mb-2 font-medium">Author</label>
        <input
          type="text"
          value={subtopic.author || ''}
          onChange={(e) => setSubtopic({ ...subtopic, author: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <label className="block mb-2 font-medium">Drive Link</label>
        <input
          type="url"
          value={subtopic.driveLink || ''}
          onChange={(e) => setSubtopic({ ...subtopic, driveLink: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <label className="block mb-2 font-medium">Blog Link</label>
        <input
          type="url"
          value={subtopic.blogLink || ''}
          onChange={(e) => setSubtopic({ ...subtopic, blogLink: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <label className="block mb-2 font-medium">Metadata</label>
        <textarea
          value={subtopic.metadata || ''}
          onChange={(e) => setSubtopic({ ...subtopic, metadata: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        />

        <label className="block mb-2 font-medium">Type</label>
        <select
          value={subtopic.dataType || 'notes'}
          onChange={(e) => setSubtopic({ ...subtopic, dataType: e.target.value })}
          className="w-full border rounded px-4 py-2 mb-4"
        >
          <option value="notes">Notes</option>
          <option value="pyqs">PYQs</option>
          <option value="blog">Blog</option>
          <option value="tutorial">Tutorial</option>
        </select>

        <button
          type="submit"
          className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 transition"
        >
          Update Subtopic
        </button>
      </form>
    </div>
  );
};

export default EditSubtopic;
